import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { FileDown, FileSpreadsheet, DollarSign, TrendingUp, Users, ShoppingBag, XCircle } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['#C2410C', '#15803D', '#B45309', '#0369A1', '#78716C', '#7C3AED', '#DC2626'];
const paymentLabels = { efectivo: 'Efectivo', tarjeta: 'Tarjeta', cortesia: 'Cortesia', cupon: 'Cupon', otro: 'Otro' };

export default function ReportsPage() {
  const { API } = useAuth();
  const [activeTab, setActiveTab] = useState('summary');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [groupBy, setGroupBy] = useState('daily');
  const [summaryData, setSummaryData] = useState(null);
  const [waiterData, setWaiterData] = useState([]);
  const [cashierData, setCashierData] = useState([]);
  const [productData, setProductData] = useState([]);
  const [cancelData, setCancelData] = useState(null);
  const [expData, setExpData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    try {
      if (activeTab === 'summary') {
        params.append('group_by', groupBy);
        const { data } = await axios.get(`${API}/reports/summary?${params}`);
        setSummaryData(data);
      } else if (activeTab === 'by-waiter') {
        const { data } = await axios.get(`${API}/reports/by-waiter?${params}`);
        setWaiterData(data);
      } else if (activeTab === 'by-cashier') {
        const { data } = await axios.get(`${API}/reports/by-cashier?${params}`);
        setCashierData(data);
      } else if (activeTab === 'by-product') {
        const { data } = await axios.get(`${API}/reports/by-product?${params}`);
        setProductData(data);
      } else if (activeTab === 'cancellations') {
        params.append('group_by', groupBy);
        const { data } = await axios.get(`${API}/reports/cancellations?${params}`);
        setCancelData(data);
      } else if (activeTab === 'experiences') {
        params.append('group_by', groupBy);
        const { data } = await axios.get(`${API}/reports/experiences?${params}`);
        setExpData(data);
      }
    } catch { toast.error('Error al cargar reporte'); }
    setLoading(false);
  }, [API, activeTab, startDate, endDate, groupBy]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleExport = async (format) => {
    const params = new URLSearchParams();
    params.append('report_type', activeTab);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    try {
      const response = await axios.get(`${API}/reports/export/${format}?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_${activeTab}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Reporte ${format.toUpperCase()} descargado`);
    } catch { toast.error('Error al exportar'); }
  };

  const groupLabels = { daily: 'Diario', weekly: 'Semanal', monthly: 'Mensual', annual: 'Anual' };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">Reportes</h1>
          <p className="text-stone-500 text-sm mt-1">Analisis de ventas y operaciones</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="export-excel-btn" onClick={() => handleExport('excel')} variant="outline" className="rounded-full border-stone-300 text-stone-700 hover:border-green-600 hover:text-green-700">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button data-testid="export-pdf-btn" onClick={() => handleExport('pdf')} variant="outline" className="rounded-full border-stone-300 text-stone-700 hover:border-red-600 hover:text-red-700">
            <FileDown className="w-4 h-4 mr-2" /> PDF
          </Button>
        </div>
      </div>

      <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div>
            <Label className="text-xs text-stone-500">Fecha inicio</Label>
            <Input data-testid="report-start-date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="rounded-xl mt-1" />
          </div>
          <div>
            <Label className="text-xs text-stone-500">Fecha fin</Label>
            <Input data-testid="report-end-date" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="rounded-xl mt-1" />
          </div>
          {activeTab === 'summary' && (
            <div>
              <Label className="text-xs text-stone-500">Agrupar por</Label>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger data-testid="report-group-by" className="rounded-xl mt-1 w-40"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(groupLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-stone-100 p-1 rounded-full h-auto">
          <TabsTrigger data-testid="tab-summary" value="summary" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-1.5 text-sm">Resumen</TabsTrigger>
          <TabsTrigger data-testid="tab-by-waiter" value="by-waiter" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-1.5 text-sm">Por Mesero</TabsTrigger>
          <TabsTrigger data-testid="tab-by-cashier" value="by-cashier" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-1.5 text-sm">Por Cajero</TabsTrigger>
          <TabsTrigger data-testid="tab-by-product" value="by-product" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-1.5 text-sm">Por Producto</TabsTrigger>
          <TabsTrigger data-testid="tab-cancellations" value="cancellations" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-1.5 text-sm">Cancelaciones</TabsTrigger>
          <TabsTrigger data-testid="tab-experiences" value="experiences" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-1.5 text-sm">Experiencias</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="mt-6 space-y-6">
          {summaryData && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center"><DollarSign className="w-5 h-5 text-orange-700" /></div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Ingresos</p>
                      <p className="text-2xl font-outfit font-semibold text-stone-900">${summaryData.totals.total_revenue.toLocaleString()}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center"><Users className="w-5 h-5 text-blue-700" /></div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Visitas</p>
                      <p className="text-2xl font-outfit font-semibold text-stone-900">{summaryData.totals.total_visits}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-green-50 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-green-700" /></div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Ticket Promedio</p>
                      <p className="text-2xl font-outfit font-semibold text-stone-900">${summaryData.totals.avg_ticket}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {summaryData.trend.length > 0 && (
                <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6">
                  <h3 className="font-outfit font-medium text-stone-900 mb-4">Tendencia de Ventas</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={summaryData.trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis dataKey="period" tick={{ fontSize: 12, fill: '#78716C' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#78716C' }} />
                      <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E7E5E4' }} />
                      <Legend />
                      <Line type="monotone" dataKey="revenue" name="Ingresos" stroke="#C2410C" strokeWidth={2} dot={{ fill: '#C2410C' }} />
                      <Line type="monotone" dataKey="visits" name="Visitas" stroke="#0369A1" strokeWidth={2} dot={{ fill: '#0369A1' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {summaryData.payment_breakdown.length > 0 && (
                <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6">
                  <h3 className="font-outfit font-medium text-stone-900 mb-4">Metodos de Pago</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={summaryData.payment_breakdown.map(p => ({ ...p, name: paymentLabels[p.method] || p.method }))} dataKey="total" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                        {summaryData.payment_breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E7E5E4' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {summaryData.visits && summaryData.visits.length > 0 && (
                <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl overflow-hidden">
                  <div className="p-4 border-b border-stone-100">
                    <h3 className="font-outfit font-medium text-stone-900">Detalle de Visitas ({summaryData.visits.length})</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="summary-visits-table">
                      <thead><tr className="bg-stone-50/50">
                        <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-stone-500">Fecha</th>
                        <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-stone-500">Cliente</th>
                        <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-stone-500">Categoria</th>
                        <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-stone-500">Mesero</th>
                        <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-stone-500">Cajero</th>
                        <th className="text-right p-3 text-xs font-semibold uppercase tracking-wider text-stone-500">Subtotal</th>
                        <th className="text-right p-3 text-xs font-semibold uppercase tracking-wider text-stone-500">Desc.</th>
                        <th className="text-right p-3 text-xs font-semibold uppercase tracking-wider text-stone-500">Total</th>
                        <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-stone-500">Pago</th>
                      </tr></thead>
                      <tbody>{summaryData.visits.map((v, i) => (
                        <tr key={i} className="border-t border-stone-100 hover:bg-stone-50/50">
                          <td className="p-3 text-xs text-stone-600">{v.closed_at ? new Date(v.closed_at).toLocaleDateString('es-MX') : ''}</td>
                          <td className="p-3 text-sm font-medium text-stone-900">{v.client_name}</td>
                          <td className="p-3 text-xs text-stone-600">{v.client_category}</td>
                          <td className="p-3 text-xs text-stone-600">{v.waiter_name}</td>
                          <td className="p-3 text-xs text-stone-600">{v.cashier_name}</td>
                          <td className="p-3 text-xs text-right text-stone-600">${(v.subtotal || 0).toFixed(2)}</td>
                          <td className="p-3 text-xs text-right text-orange-700">{v.discount_percent > 0 ? `${v.discount_percent}%` : '-'}</td>
                          <td className="p-3 text-sm text-right font-semibold text-stone-900">${(v.total || 0).toFixed(2)}</td>
                          <td className="p-3 text-xs text-stone-600">{paymentLabels[v.payment_method] || v.payment_method}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                </Card>
              )}
            </>
          )}
          {loading && <p className="text-stone-400 text-center py-8">Cargando...</p>}
        </TabsContent>

        <TabsContent value="by-waiter" className="mt-6 space-y-6">
          {waiterData.length > 0 && (
            <>
              <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6">
                <h3 className="font-outfit font-medium text-stone-900 mb-4">Ventas por Mesero</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={waiterData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                    <XAxis dataKey="waiter_name" tick={{ fontSize: 12, fill: '#78716C' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#78716C' }} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E7E5E4' }} />
                    <Bar dataKey="total_revenue" name="Ingresos" fill="#C2410C" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl overflow-hidden">
                <table className="w-full" data-testid="waiter-report-table">
                  <thead><tr className="bg-stone-50/50">
                    <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Mesero</th>
                    <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Ingresos</th>
                    <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Visitas</th>
                    <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Ticket Prom.</th>
                  </tr></thead>
                  <tbody>{waiterData.map(w => (
                    <tr key={w.waiter_id} className="border-t border-stone-100"><td className="p-4 text-sm font-medium text-stone-900">{w.waiter_name}</td><td className="p-4 text-sm text-right text-stone-700">${w.total_revenue.toFixed(2)}</td><td className="p-4 text-sm text-right text-stone-700">{w.total_visits}</td><td className="p-4 text-sm text-right text-stone-700">${w.avg_ticket.toFixed(2)}</td></tr>
                  ))}</tbody>
                </table>
              </Card>
            </>
          )}
          {waiterData.length === 0 && !loading && <p className="text-stone-400 text-center py-8">Sin datos de meseros</p>}
        </TabsContent>

        <TabsContent value="by-cashier" className="mt-6 space-y-6">
          {cashierData.length > 0 && (
            <>
              <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6">
                <h3 className="font-outfit font-medium text-stone-900 mb-4">Cobros por Cajero</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={cashierData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                    <XAxis dataKey="cashier_name" tick={{ fontSize: 12, fill: '#78716C' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#78716C' }} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E7E5E4' }} />
                    <Bar dataKey="total_revenue" name="Ingresos" fill="#15803D" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl overflow-hidden">
                <table className="w-full" data-testid="cashier-report-table">
                  <thead><tr className="bg-stone-50/50">
                    <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Cajero</th>
                    <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Ingresos</th>
                    <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Cobros</th>
                  </tr></thead>
                  <tbody>{cashierData.map(c => (
                    <tr key={c.cashier_id} className="border-t border-stone-100"><td className="p-4 text-sm font-medium text-stone-900">{c.cashier_name}</td><td className="p-4 text-sm text-right text-stone-700">${c.total_revenue.toFixed(2)}</td><td className="p-4 text-sm text-right text-stone-700">{c.total_checkouts}</td></tr>
                  ))}</tbody>
                </table>
              </Card>
            </>
          )}
          {cashierData.length === 0 && !loading && <p className="text-stone-400 text-center py-8">Sin datos de cajeros</p>}
        </TabsContent>

        <TabsContent value="by-product" className="mt-6 space-y-6">
          {productData.length > 0 && (
            <>
              <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6">
                <h3 className="font-outfit font-medium text-stone-900 mb-4">Ventas por Producto</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={productData.slice(0, 10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                    <XAxis type="number" tick={{ fontSize: 12, fill: '#78716C' }} />
                    <YAxis dataKey="product_name" type="category" width={120} tick={{ fontSize: 12, fill: '#78716C' }} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E7E5E4' }} />
                    <Bar dataKey="total_revenue" name="Ingresos" fill="#B45309" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl overflow-hidden">
                <table className="w-full" data-testid="product-report-table">
                  <thead><tr className="bg-stone-50/50">
                    <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Producto</th>
                    <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Cantidad</th>
                    <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Ingresos</th>
                  </tr></thead>
                  <tbody>{productData.map(p => (
                    <tr key={p.product_id} className="border-t border-stone-100"><td className="p-4 text-sm font-medium text-stone-900">{p.product_name}</td><td className="p-4 text-sm text-right text-stone-700">{p.total_quantity}</td><td className="p-4 text-sm text-right text-stone-700">${p.total_revenue.toFixed(2)}</td></tr>
                  ))}</tbody>
                </table>
              </Card>
            </>
          )}
          {productData.length === 0 && !loading && <p className="text-stone-400 text-center py-8">Sin datos de productos</p>}
        </TabsContent>

        <TabsContent value="cancellations" className="mt-6 space-y-6">
          {cancelData && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center"><XCircle className="w-5 h-5 text-red-700" /></div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Cancelaciones</p>
                      <p className="text-2xl font-outfit font-semibold text-stone-900">{cancelData.totals.total_count}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center"><DollarSign className="w-5 h-5 text-red-700" /></div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Perdida Total</p>
                      <p className="text-2xl font-outfit font-semibold text-red-700">${cancelData.totals.total_lost.toLocaleString()}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {cancelData.trend.length > 0 && (
                <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6">
                  <h3 className="font-outfit font-medium text-stone-900 mb-4">Tendencia de Cancelaciones</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={cancelData.trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis dataKey="period" tick={{ fontSize: 12, fill: '#78716C' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#78716C' }} />
                      <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E7E5E4' }} />
                      <Bar dataKey="count" name="Cancelaciones" fill="#DC2626" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {cancelData.by_reason.length > 0 && (
                  <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6">
                    <h3 className="font-outfit font-medium text-stone-900 mb-4">Por Razon</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={cancelData.by_reason} dataKey="count" nameKey="reason" cx="50%" cy="50%" outerRadius={80} label={({ reason, percent }) => `${reason.substring(0,15)} ${(percent*100).toFixed(0)}%`}>
                          {cancelData.by_reason.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E7E5E4' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Card>
                )}
                {cancelData.by_product.length > 0 && (
                  <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6">
                    <h3 className="font-outfit font-medium text-stone-900 mb-4">Por Producto</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={cancelData.by_product.slice(0,8)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                        <XAxis type="number" tick={{ fontSize: 12, fill: '#78716C' }} />
                        <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11, fill: '#78716C' }} />
                        <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E7E5E4' }} />
                        <Bar dataKey="count" name="Cancelaciones" fill="#B91C1C" radius={[0, 8, 8, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                )}
              </div>

              <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl overflow-hidden">
                <table className="w-full" data-testid="cancellations-table">
                  <thead><tr className="bg-stone-50/50">
                    <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Fecha</th>
                    <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Producto</th>
                    <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Cliente</th>
                    <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Razon</th>
                    <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Cancelado por</th>
                    <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Total</th>
                  </tr></thead>
                  <tbody>{(cancelData.cancellations || []).map(c => (
                    <tr key={c.id} className="border-t border-stone-100">
                      <td className="p-4 text-sm text-stone-600">{c.cancelled_at ? new Date(c.cancelled_at).toLocaleDateString('es-MX') : ''}</td>
                      <td className="p-4 text-sm font-medium text-stone-900">{c.product_name} x{c.quantity}</td>
                      <td className="p-4 text-sm text-stone-600">{c.client_name}</td>
                      <td className="p-4 text-sm text-red-700">{c.reason}</td>
                      <td className="p-4 text-sm text-stone-600">{c.cancelled_by_name}</td>
                      <td className="p-4 text-sm text-right font-medium text-stone-900">${(c.total || 0).toFixed(2)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </Card>
            </>
          )}
          {(!cancelData || cancelData.totals.total_count === 0) && !loading && <p className="text-stone-400 text-center py-8">Sin cancelaciones registradas</p>}
        </TabsContent>

        <TabsContent value="experiences" className="mt-6 space-y-6">
          {expData && expData.totals.total_count > 0 && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-purple-50 flex items-center justify-center"><DollarSign className="w-5 h-5 text-purple-700" /></div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Cortesias Aplicadas</p>
                      <p className="text-2xl font-outfit font-semibold text-stone-900">{expData.totals.total_count}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-purple-50 flex items-center justify-center"><DollarSign className="w-5 h-5 text-purple-700" /></div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Monto Total</p>
                      <p className="text-2xl font-outfit font-semibold text-purple-700">${expData.totals.total_amount.toLocaleString()}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {expData.trend.length > 0 && (
                <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6">
                  <h3 className="font-outfit font-medium text-stone-900 mb-4">Tendencia de Cortesias</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={expData.trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis dataKey="period" tick={{ fontSize: 12, fill: '#78716C' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#78716C' }} />
                      <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E7E5E4' }} />
                      <Bar dataKey="count" name="Cortesias" fill="#7C3AED" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {expData.by_type.length > 0 && (
                  <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6">
                    <h3 className="font-outfit font-medium text-stone-900 mb-4">Por Tipo</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={expData.by_type} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name.substring(0,18)} ${(percent*100).toFixed(0)}%`}>
                          {expData.by_type.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E7E5E4' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Card>
                )}
                {expData.by_reason.length > 0 && (
                  <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6">
                    <h3 className="font-outfit font-medium text-stone-900 mb-4">Por Razon</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={expData.by_reason.slice(0,8)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                        <XAxis type="number" tick={{ fontSize: 12, fill: '#78716C' }} />
                        <YAxis dataKey="reason" type="category" width={130} tick={{ fontSize: 11, fill: '#78716C' }} />
                        <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E7E5E4' }} />
                        <Bar dataKey="count" name="Cortesias" fill="#9333EA" radius={[0, 8, 8, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                )}
              </div>

              <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl overflow-hidden">
                <div className="p-4 border-b border-stone-100">
                  <h3 className="font-outfit font-medium text-stone-900">Detalle de Cortesias</h3>
                </div>
                <table className="w-full" data-testid="experiences-table">
                  <thead><tr className="bg-stone-50/50">
                    <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Fecha</th>
                    <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Tipo</th>
                    <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Cliente</th>
                    <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Producto</th>
                    <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Razon</th>
                    <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Aplicado por</th>
                    <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Monto</th>
                  </tr></thead>
                  <tbody>{(expData.experiences || []).map(e => (
                    <tr key={e.id} className="border-t border-stone-100">
                      <td className="p-4 text-sm text-stone-600">{e.applied_at ? new Date(e.applied_at).toLocaleDateString('es-MX') : ''}</td>
                      <td className="p-4 text-sm text-purple-700 font-medium">{e.type_label}</td>
                      <td className="p-4 text-sm text-stone-900">{e.client_name}</td>
                      <td className="p-4 text-sm text-stone-600">{e.product_name || '-'}</td>
                      <td className="p-4 text-sm text-stone-600">{e.reason}</td>
                      <td className="p-4 text-sm text-stone-600">{e.applied_by_name}</td>
                      <td className="p-4 text-sm text-right font-medium text-purple-700">${(e.original_amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </Card>
            </>
          )}
          {(!expData || expData.totals.total_count === 0) && !loading && <p className="text-stone-400 text-center py-8">Sin cortesias registradas</p>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
